import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, debounce, Paper, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { contextObject } from "../../components/shared/ShoppingCart/ShoppingCartContext";
import { UserContext } from "../../hooks/userContext";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import { CART, PRODUCTS_PAGINATED } from "../../queries/product/productTypeDefs";
import { useMutationCreateCart } from "../../queries/product/hooks/useMutationCreateCart";
import { useMutationUpdateCart } from "../../queries/product/hooks/useMutationUpdateCart";
import { ProductDialog } from "../../components/shared/ProductDialog/ProductDialog";
import { CustomColumnMenu } from "../../components/shared/CustomColumnMenu/CustomColumnMenu";
import { routeConfigs } from "../../navigation/routeConfig";
import {
  attachStickyColumnScrollListener,
  BuyGridPagination,
  getProductListGridColumns,
  mapProductsToGridRows,
  PRODUCT_LIST_PAGE_SIZE,
  productListDataGridSx,
} from "../Buy/buyProductGrid";
import { marketplaceBuyableWhere } from "../Buy/productListWhere";

function parseExcludeIds(raw: string | null): number[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export const SellerListings: React.FunctionComponent = () => {
  const { sellerUserId } = useParams<{ sellerUserId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id }: any = useContext(UserContext);
  const { cart } = useContext(contextObject);
  const { addToast } = useContext(ToasterContext);

  const excludeKey = searchParams.get("exclude") || "";
  const excludeIds = useMemo(() => parseExcludeIds(excludeKey || null), [excludeKey]);

  const sellerIdNum = sellerUserId != null ? Number(sellerUserId) : NaN;
  const sellerValid = Number.isFinite(sellerIdNum) && sellerIdNum > 0;

  const baseWhere = useMemo(
    () => ({
      ...marketplaceBuyableWhere,
      User: { is: { id: { equals: sellerIdNum } } },
      ...(excludeIds.length ? { id: { notIn: excludeIds } } : {}),
    }),
    [sellerIdNum, excludeIds]
  );

  const [searchInput, setSearchInput] = useState("");
  const [currentWhere, setCurrentWhere] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<any>(null);

  useEffect(() => {
    if (!sellerValid) return;
    setPage(0);
    setSearchInput("");
    setCurrentWhere(baseWhere);
  }, [sellerValid, baseWhere]);

  const search = useMemo(
    () =>
      debounce((value: string) => {
        setPage(0);
        if (value !== "") {
          setCurrentWhere({
            ...baseWhere,
            OR: [
              { drugName: { contains: value, mode: "insensitive" } },
              { ndc: { contains: value, mode: "insensitive" } },
            ],
          });
        } else {
          setCurrentWhere(baseWhere);
        }
      }, 1000),
    [baseWhere]
  );

  useEffect(() => {
    return () => search.clear();
  }, [search]);

  const { data, loading: queryLoading } = useQuery(PRODUCTS_PAGINATED, {
    variables: {
      where: currentWhere ?? baseWhere,
      orderBy: { createdAt: "desc" },
      skip: page * PRODUCT_LIST_PAGE_SIZE,
      take: PRODUCT_LIST_PAGE_SIZE,
    },
    skip: !sellerValid || !currentWhere,
    fetchPolicy: "no-cache",
    pollInterval: 5000,
  });

  const paginatedResult = data?.findManyProductPaginated;
  const lastKnownCount = useRef(0);
  if (paginatedResult?.totalCount != null) {
    lastKnownCount.current = paginatedResult.totalCount;
  }
  const totalRows = lastKnownCount.current;

  const [createCart, { loading: createCartLoading }] = useMutationCreateCart({
    refetchQueries: [
      {
        query: CART,
        variables: { where: { userId: { equals: +id } }, orderBy: { createdAt: "asc" } },
        awaitRefetchQueries: true,
      },
    ],
  });
  const [updateCart, { loading: updateCartLoading }] = useMutationUpdateCart({
    refetchQueries: [
      {
        query: CART,
        variables: { where: { userId: { equals: +id } }, orderBy: { createdAt: "asc" } },
        awaitRefetchQueries: true,
      },
    ],
  });

  const cartLoading = createCartLoading || updateCartLoading;

  const handleCart = (p: any, packageAvailable: number = 1) => {
    if (cartLoading) return;
    const requestData = {
      quantity: p.row.available,
      User: { connect: { id: +id } },
      Product: { connect: { id: p.id } },
    };
    const foundCartItem: any = cart.find((cartItem: any) => cartItem.item.id === p.id);
    const newCount = packageAvailable + (foundCartItem?.quantity || 0);
    if (newCount > p.row.available) {
      addToast({ type: NotificationType.Error, title: `max available count: ${p.row.available}` });
      return;
    }
    if (foundCartItem) {
      updateCart({
        variables: { data: { ...requestData, quantity: { increment: packageAvailable } }, where: { id: foundCartItem.id } },
      })
        .then(() => addToast({ type: NotificationType.Success, title: "Cart updated successfully" }))
        .catch((error: any) => addToast({ type: NotificationType.Error, title: error.message }));
    } else {
      createCart({ variables: { data: { ...requestData, quantity: packageAvailable } } }).catch((error: any) => {
        addToast({ type: NotificationType.Error, title: error.message });
      });
    }
  };

  const columns = useMemo(
    () =>
      getProductListGridColumns({
        currentUserId: +id,
        updateCartLoading: cartLoading,
        handleCart,
      }),
    [id, cartLoading]
  );

  useEffect(() => attachStickyColumnScrollListener(), []);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    search(value);
  };

  if (!sellerValid) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Invalid seller.</Typography>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackIcon />} onClick={() => navigate(routeConfigs.checkout.path)}>
          Back to checkout
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, pb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} variant="outlined" size="small" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h6" component="h1">
            More from this seller
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active listings with inventory ({totalRows} {totalRows === 1 ? "item" : "items"})
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search by Drug name or NDC..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minHeight: "30px", minWidth: 300 }}
        />
      </Box>

      <Box sx={{ height: "calc(100vh - 236px)", width: "100%" }}>
        <DataGrid
          loading={queryLoading}
          rows={mapProductsToGridRows(paginatedResult?.products)}
          columns={columns}
          slots={{ columnMenu: CustomColumnMenu, pagination: BuyGridPagination }}
          slotProps={{ pagination: { rowCount: totalRows } as any }}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={{ page, pageSize: PRODUCT_LIST_PAGE_SIZE }}
          onPaginationModelChange={(model) => setPage(model.page)}
          onRowClick={(rowParams, event: any) => {
            if (event.target.tagName === "DIV" || event.target.tagName === "SPAN") {
              setParams(rowParams);
              setOpen(true);
            }
          }}
          pageSizeOptions={[PRODUCT_LIST_PAGE_SIZE]}
          sx={productListDataGridSx}
        />
      </Box>

      {open && (
        <ProductDialog
          params={params}
          setParams={setParams}
          open={open}
          setOpen={setOpen}
          handleCart={handleCart}
          loading={cartLoading}
        />
      )}
    </Paper>
  );
};

export default SellerListings;
